import { useState, useCallback, useRef, useEffect } from "react";
import { Upload, X, Image as ImageIcon, Move } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ImageUploadProps {
  vehicleId: string;
  onImagesUploaded: () => void;
}

export function ImageUpload({ vehicleId, onImagesUploaded }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const checkFile = (file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      // 1. Max size 5MB (MED-11)
      if (file.size > 5 * 1024 * 1024) {
        reject(new Error(`"${file.name}" exceeds the 5MB size limit.`));
        return;
      }

      // 2. Type validation (MED-11)
      const allowedMimes = ["image/jpeg", "image/png", "image/webp"];
      const allowedExts = ["jpg", "jpeg", "png", "webp"];
      const ext = file.name.split(".").pop()?.toLowerCase() || "";
      if (!allowedMimes.includes(file.type) && !allowedExts.includes(ext)) {
        reject(new Error(`"${file.name}" has an unsupported format. Only JPEG, PNG, and WEBP are allowed.`));
        return;
      }

      // 3. Dimension validation (MED-11)
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (img.naturalWidth < 400 || img.naturalHeight < 300) {
          reject(new Error(`"${file.name}" dimensions are too small (min 400x300). Current: ${img.naturalWidth}x${img.naturalHeight}`));
        } else if (img.naturalWidth > 8000 || img.naturalHeight > 8000) {
          reject(new Error(`"${file.name}" dimensions are too large (max 8000x8000). Current: ${img.naturalWidth}x${img.naturalHeight}`));
        } else {
          resolve();
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error(`"${file.name}" is not a valid image file.`));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const uploadImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const fileList = Array.from(files);
      
      // Validate all files first (MED-11)
      for (const file of fileList) {
        await checkFile(file);
      }

      const uploadPromises = fileList.map(async (file) => {
        const fileExt = file.name.split(".").pop();
        const fileName = `${vehicleId}/${Math.random()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("vehicle-images")
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from("vehicle-images")
          .getPublicUrl(fileName);

        const { error: dbError } = await supabase.from("vehicle_images").insert({
          vehicle_id: vehicleId,
          image_url: data.publicUrl,
          display_order: 0,
        });

        if (dbError) throw dbError;
      });

      await Promise.all(uploadPromises);
      toast.success(`${files.length} image(s) uploaded successfully`);
      onImagesUploaded();
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload images");
    } finally {
      setUploading(false);
      setDragActive(false);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const { files } = e.dataTransfer;
      uploadImages(files);
    },
    [vehicleId]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    uploadImages(e.target.files);
  };

  return (
    <div
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
        ? "border-primary bg-primary/5"
        : "border-border hover:border-primary/50"
        }`}
    >
      <input
        type="file"
        multiple
        accept="image/*"
        onChange={handleChange}
        disabled={uploading}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
      />
      <div className="flex flex-col items-center gap-2">
        <div className="p-4 rounded-full bg-primary/10">
          <Upload className="h-8 w-8 text-primary" />
        </div>
        <div>
          <p className="text-sm font-medium">
            {uploading ? "Uploading..." : "Drop images here or click to browse"}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            PNG, JPG, WEBP up to 5MB
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Position Editor Modal ──────────────────────────────────────────────────

interface PositionEditorProps {
  imageUrl: string;
  currentPosition: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (position: string) => void;
}

function PositionEditor({ imageUrl, currentPosition, open, onOpenChange, onSave }: PositionEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [saving, setSaving] = useState(false);

  // Parse the current position on open
  useEffect(() => {
    if (open) {
      const parsed = parseCssPosition(currentPosition);
      setPosition(parsed);
    }
  }, [open, currentPosition]);

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updatePosition(e);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    updatePosition(e);
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  const updatePosition = (e: React.PointerEvent) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setPosition({ x: Math.round(x), y: Math.round(y) });
  };

  const handleSave = async () => {
    setSaving(true);
    const posStr = `${position.x}% ${position.y}%`;
    await onSave(posStr);
    setSaving(false);
    onOpenChange(false);
  };

  const handleReset = () => {
    setPosition({ x: 50, y: 50 });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Adjust Image Position</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Click or drag on the image to set the focal point. This determines which part of the image stays visible when cropped to the 4:3 frame.
        </p>
        {/* Preview container: shows the image in a 4:3 frame with the current position */}
        <div className="space-y-3">
          <div
            ref={containerRef}
            className="relative aspect-[4/3] overflow-hidden rounded-lg border-2 border-dashed border-primary/30 cursor-crosshair select-none"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            style={{ touchAction: 'none' }}
          >
            <img
              src={imageUrl}
              alt="Position preview"
              className="w-full h-full object-cover pointer-events-none"
              style={{ objectPosition: `${position.x}% ${position.y}%` }}
              draggable={false}
            />
            {/* Focal point crosshair indicator */}
            <div
              className="absolute w-8 h-8 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10"
              style={{ left: `${position.x}%`, top: `${position.y}%` }}
            >
              <div className="absolute inset-0 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]" />
              <div className="absolute top-1/2 left-0 w-full h-px bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.3)]" />
              <div className="absolute left-1/2 top-0 h-full w-px bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.3)]" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Position: {position.x}% {position.y}%
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReset}>
                Reset to Center
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Position"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Parse a CSS object-position string like "50% 30%" or "center" into {x, y} percentages */
function parseCssPosition(pos: string): { x: number; y: number } {
  if (!pos || pos === 'center') return { x: 50, y: 50 };

  const keywords: Record<string, { x: number; y: number }> = {
    'top': { x: 50, y: 0 },
    'bottom': { x: 50, y: 100 },
    'left': { x: 0, y: 50 },
    'right': { x: 100, y: 50 },
    'top left': { x: 0, y: 0 },
    'top right': { x: 100, y: 0 },
    'bottom left': { x: 0, y: 100 },
    'bottom right': { x: 100, y: 100 },
  };

  if (keywords[pos]) return keywords[pos];

  const parts = pos.split(/\s+/);
  if (parts.length >= 2) {
    const x = parseFloat(parts[0]);
    const y = parseFloat(parts[1]);
    if (!isNaN(x) && !isNaN(y)) return { x, y };
  }

  return { x: 50, y: 50 };
}

// ─── Vehicle Images Grid ────────────────────────────────────────────────────

interface VehicleImagesProps {
  vehicleId: string;
  images: Array<{ id: string; image_url: string; is_primary: boolean; object_position?: string }>;
  onImagesChanged: () => void;
}

export function VehicleImages({
  vehicleId,
  images,
  onImagesChanged,
}: VehicleImagesProps) {
  const [deleting, setDeleting] = useState<string | null>(null);
  const [localImages, setLocalImages] = useState(images);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [positionEditorTarget, setPositionEditorTarget] = useState<{
    id: string;
    image_url: string;
    object_position: string;
  } | null>(null);

  // Sync local state when parent refreshes (but not while dragging)
  if (images !== localImages && !draggingId) {
    setLocalImages(images);
  }

  const deleteImage = async (imageId: string, imageUrl: string) => {
    setDeleting(imageId);
    try {
      // Extract filename from URL
      const fileName = imageUrl.split("/vehicle-images/")[1];

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("vehicle-images")
        .remove([fileName]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("vehicle_images")
        .delete()
        .eq("id", imageId);

      if (dbError) throw dbError;

      toast.success("Image deleted");
      onImagesChanged();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete image");
    } finally {
      setDeleting(null);
    }
  };

  const setPrimaryImage = async (imageId: string) => {
    try {
      // Reset all images for this vehicle
      await supabase
        .from("vehicle_images")
        .update({ is_primary: false })
        .eq("vehicle_id", vehicleId);

      // Set the selected one as primary
      const { error } = await supabase
        .from("vehicle_images")
        .update({ is_primary: true })
        .eq("id", imageId);

      if (error) throw error;

      toast.success("Primary image updated");
      onImagesChanged();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to set primary image");
    }
  };

  const saveImagePosition = async (position: string) => {
    if (!positionEditorTarget) return;
    try {
      const { error } = await supabase
        .from("vehicle_images")
        .update({ object_position: position })
        .eq("id", positionEditorTarget.id);

      if (error) throw error;

      toast.success("Image position saved");
      onImagesChanged();
    } catch (error) {
      console.error("Position save error:", error);
      toast.error("Failed to save image position");
    }
  };

  // --- Drag-and-drop reorder ---
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== draggingId) setDragOverId(id);
  };

  const handleDragLeave = () => setDragOverId(null);

  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDragOverId(null);
    setDraggingId(null);

    const sourceId = e.dataTransfer.getData("text/plain");
    if (!sourceId || sourceId === targetId) return;

    // Optimistic reorder
    const reordered = [...localImages];
    const sourceIdx = reordered.findIndex((img) => img.id === sourceId);
    const targetIdx = reordered.findIndex((img) => img.id === targetId);
    const [moved] = reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    setLocalImages(reordered);

    // Persist to Supabase
    setSaving(true);
    try {
      await Promise.all(
        reordered.map((img, idx) =>
          supabase
            .from("vehicle_images")
            .update({ display_order: idx })
            .eq("id", img.id)
        )
      );
      toast.success("Image order saved");
      onImagesChanged();
    } catch (error) {
      console.error("Reorder error:", error);
      toast.error("Failed to save order");
      setLocalImages(images); // revert on error
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  if (localImages.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 border-2 border-dashed rounded-lg">
        <div className="text-center">
          <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No images uploaded yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <span className="text-base leading-none">⠿</span>
        Drag images to reorder — first image shows on listings.
        {saving && <span className="text-primary font-medium ml-1">Saving...</span>}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {localImages.map((image, index) => {
          const pos = (image as any).object_position || 'center';
          return (
            <div
              key={image.id}
              draggable
              onDragStart={(e) => handleDragStart(e, image.id)}
              onDragOver={(e) => handleDragOver(e, image.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, image.id)}
              onDragEnd={handleDragEnd}
              className={`relative group rounded-lg transition-all duration-150 cursor-grab active:cursor-grabbing ${draggingId === image.id ? "opacity-40 scale-95" : "opacity-100"
                } ${dragOverId === image.id
                  ? "ring-2 ring-primary ring-offset-2 scale-[1.03]"
                  : ""
                }`}
            >
              {/* Order badge + drag handle */}
              <div className="absolute top-2 left-2 z-10">
                <div className="bg-black/60 text-white rounded px-1.5 py-0.5 text-xs select-none flex items-center gap-1">
                  <span>⠿</span>
                  <span>{index + 1}</span>
                </div>
              </div>

              <img
                src={image.image_url}
                alt="Vehicle"
                className="w-full aspect-[4/3] object-cover rounded-lg pointer-events-none"
                style={pos !== 'center' ? { objectPosition: pos } : undefined}
              />

              {image.is_primary && (
                <div className="absolute top-2 right-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                  Primary
                </div>
              )}

              {/* Position indicator badge */}
              {pos !== 'center' && (
                <div className="absolute bottom-2 left-2 bg-blue-600/80 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Move className="h-3 w-3" />
                  <span>Repositioned</span>
                </div>
              )}

              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPositionEditorTarget({
                    id: image.id,
                    image_url: image.image_url,
                    object_position: pos,
                  })}
                >
                  <Move className="h-3.5 w-3.5 mr-1" />
                  Position
                </Button>
                {!image.is_primary && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setPrimaryImage(image.id)}
                  >
                    Set Primary
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => deleteImage(image.id, image.image_url)}
                  disabled={deleting === image.id}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Position Editor Modal */}
      {positionEditorTarget && (
        <PositionEditor
          imageUrl={positionEditorTarget.image_url}
          currentPosition={positionEditorTarget.object_position}
          open={!!positionEditorTarget}
          onOpenChange={(open) => { if (!open) setPositionEditorTarget(null); }}
          onSave={saveImagePosition}
        />
      )}
    </div>
  );
}
